import { Link, useRouterState } from "../lib/router-compat";
import { useEffect, useState } from "react";
import { Menu as MenuIcon, X } from "lucide-react";
import { OverlayLink } from "./OverlayLink";

type MenuItem = { label: string; to: string };
type MenuGroup = { heading: string; items: MenuItem[] };

const menuGroups: MenuGroup[] = [
  {
    heading: "Programs",
    items: [
      { label: "Education", to: "/classes" },
      { label: "Maine Pan Productions", to: "/maine-pan-production" },
      { label: "Atlantic Clarion", to: "/atlantic-clarion" },
      { label: "Pan Coalition", to: "/pan-coalition" },
      { label: "Planet Pan", to: "/planet-pan" },
    ],
  },
  {
    heading: "Performances",
    items: [
      { label: "Calendar", to: "/calendar" },
      { label: "Monday Nights!", to: "/monday-nights" },
      { label: "Annual Pan Festival", to: "/pan-festival" },
      { label: "Recordings", to: "/recordings" },
    ],
  },
  {
    heading: "Interactive",
    items: [
      { label: "Tutorial", to: "/learn-to-play" },
      { label: "Visualizer", to: "/visualizer" },
    ],
  },
  {
    heading: "Connect",
    items: [
      { label: "Sheet Music", to: "/sheet-music" },
      { label: "History of Pan in Maine", to: "/history" },
      { label: "Nigel P. Chase", to: "/nigel" },
      { label: "Contact", to: "/contact" },
    ],
  },
];

export function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    // Only manage body overflow when the overlay isn't handling it already
    if (document.body.classList.contains("pi-overlay-open")) return;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      if (!document.body.classList.contains("pi-overlay-open")) {
        document.body.style.overflow = "";
      }
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-[160] transition-[background,backdrop-filter,border-color] duration-300 ${
          scrolled || open
            ? "bg-background/70 backdrop-blur-2xl border-b border-border/40"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 md:px-8">
          <Link
            to="/"
            aria-label="Pan Institute — home"
            className="text-sm md:text-base uppercase tracking-[0.4em] text-white hover:text-white/80 transition-colors whitespace-nowrap"
          >
            Pan Institute
          </Link>

          <div className="flex items-center gap-3 md:gap-5">
            <button
              type="button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/40 px-3.5 py-2 text-[10px] uppercase tracking-[0.3em] text-foreground/80 hover:text-foreground hover:border-foreground/40 transition-colors"
            >
              {open ? <X className="h-3.5 w-3.5" /> : <MenuIcon className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{open ? "Close" : "Menu"}</span>
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div
          className="fixed inset-0 z-[155] bg-background/100 backdrop-blur-2xl animate-in fade-in duration-200"
          role="dialog"
          aria-modal="true"
        >
          <div className="h-full w-full overflow-y-auto pt-24 pb-16 px-6 md:px-12">
            <div className="mx-auto max-w-6xl grid gap-12 md:grid-cols-4">
              {menuGroups.map((g) => (
                <div key={g.heading}>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-foreground/40 mb-5">
                    {g.heading}
                  </div>
                  <ul className="space-y-3">
                  {g.items.map((it) => {
                      const isFull = it.to === "/learn-to-play" || it.to === "/visualizer";
                      const className = "font-sans text-sm md:text-base font-light tracking-tight leading-tight text-foreground/85 hover:text-foreground transition-colors";
                      return (
                        <li key={it.label}>
                          {isFull ? (
                            <Link to={it.to} onClick={() => setOpen(false)} className={className}>
                              {it.label}
                            </Link>
                          ) : (
                            <OverlayLink to={it.to} onClick={() => setOpen(false)} className={className}>
                              {it.label}
                            </OverlayLink>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
